import { API_ENDPOINTS } from '@/api/endpoints';
import dropDownIcon from '@/assets/icons/drop-down-icon.png';
import { ConfirmationDialog } from '@/organization/landing/components/ConfirmationDialog';
import { type Subproject as CardSubproject } from '@/organization/landing/components/projectCard/types';
import { getOverallStatus } from '@/organization/landing/components/projectCard/utils';
import { useOrg } from '@/organization/useOrg';
import { useOrganizationPerms } from '@/organization/useOrganizationRole';
import { type Subproject } from '@/stores/SubprojectStore';
import { apiFetch } from '@/util/apiClient';
import { Edit, ErrorOutline, Star } from '@mui/icons-material';
import {
  alpha,
  Box,
  Button,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { lighten } from '@mui/system';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { SubprojectDropdownCard } from './SubprojectDropdownCard';
import { SubprojectProgressGrid } from './SubprojectProgressGrid';
import { SubprojectProgressSummary } from './SubprojectProgressSummary';

/** Cast SubprojectStore.Subproject to projectCard Subproject for getOverallStatus */
function toCardSubproject(s: Subproject): CardSubproject {
  return s as unknown as CardSubproject;
}

const FONT_HEADER = 'Montserrat, sans-serif';

const DropDownIconComponent = (props: { className?: string }) => (
  <Box
    component='img'
    src={dropDownIcon}
    alt=''
    className={props.className}
    sx={{
      width: 18,
      height: 'auto',
      filter: 'brightness(0) invert(1)',
      pointerEvents: 'none',
    }}
  />
);

interface DecarbHeaderBarProps {
  orgName: string;
  projectName: string;
  fiscalYearName?: string;
  subprojects: Subproject[];
  currentSubprojectId?: string;
  projectId?: string;
  pencilsDown?: boolean;
  designApproved?: boolean;
  budgetApproved?: boolean;
  onPmPreferenceClick?: () => void;
  onBoApprovalClick?: () => void;
  onSubprojectChange: (subprojectId: string) => void;
  onPencilsDownToggle?: () => void;
}

export function DecarbHeaderBar({
  orgName,
  projectName,
  fiscalYearName,
  subprojects,
  currentSubprojectId,
  projectId,
  pencilsDown,
  designApproved,
  budgetApproved,
  onPmPreferenceClick,
  onBoApprovalClick,
  onSubprojectChange,
  onPencilsDownToggle,
}: DecarbHeaderBarProps) {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { selectedOrg } = useOrg();
  const { data: perms } = useOrganizationPerms(selectedOrg?.id);
  const [pencilsDownDialogOpen, setPencilsDownDialogOpen] = useState(false);

  const isOverviewSelected = !currentSubprojectId;
  const currentSubproject = currentSubprojectId
    ? subprojects.find((s) => s.id === currentSubprojectId)
    : undefined;

  const allComplete =
    subprojects.length > 0 &&
    subprojects.every(
      (s) => getOverallStatus(toCardSubproject(s)) === 'finished',
    );

  const allPencilsDown =
    subprojects.length > 0 && subprojects.every((s) => s.pencilsDown);

  // For individual subproject: is THIS subproject's stage work complete?
  const currentSubprojectComplete = currentSubproject
    ? getOverallStatus(toCardSubproject(currentSubproject)) === 'finished'
    : false;

  // Pencils down button visibility: overview or individual subproject page
  const canShowPencilsDown =
    (perms?.canApproveDesign || perms?.canApproveBudget) &&
    (() => {
      if (isOverviewSelected) {
        return allComplete || pencilsDown;
      }
      return currentSubprojectComplete || currentSubproject?.pencilsDown;
    })();

  // Determine current pencils-down state for the active context
  const activePencilsDown = isOverviewSelected
    ? pencilsDown
    : currentSubproject?.pencilsDown;

  const handlePencilsDown = async () => {
    setPencilsDownDialogOpen(false);
    if (!selectedOrg?.id || !projectId) return;

    try {
      if (isOverviewSelected) {
        // Bulk toggle all subprojects via project endpoint
        const method = pencilsDown ? 'DELETE' : 'POST';
        await apiFetch(
          API_ENDPOINTS.ORGANIZATIONS.PROJECTS.PENCILS_DOWN(
            selectedOrg.id,
            projectId,
          ),
          { method },
        );
      } else if (currentSubprojectId) {
        // Toggle individual subproject
        const method = currentSubproject?.pencilsDown ? 'DELETE' : 'POST';
        await apiFetch(
          API_ENDPOINTS.ORGANIZATIONS.SUBPROJECTS.PENCILS_DOWN(
            selectedOrg.id,
            currentSubprojectId,
          ),
          { method },
        );
      }
      await queryClient.invalidateQueries({ queryKey: ['projects'] });
      await queryClient.invalidateQueries({
        queryKey: ['projects-with-subprojects'],
      });
      await queryClient.invalidateQueries({
        queryKey: ['project', selectedOrg.id, projectId],
      });
      onPencilsDownToggle?.();
    } catch (error) {
      console.error('Failed to toggle Pencils Down:', error);
    }
  };

  const finishedCount = subprojects.filter(
    (s) => getOverallStatus(toCardSubproject(s)) === 'finished',
  ).length;

  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    onSubprojectChange(e.target.value);
  };

  // Tooltip messages for disabled buttons
  const pencilsDownDisabledTooltip =
    activePencilsDown && designApproved
      ? 'Cannot undo Pencils Down while PM Preference is set'
      : '';

  const pmPreferenceDisabledTooltip = (() => {
    if (budgetApproved)
      return 'Cannot change PM Preference while BO Approval is set';
    if (!allPencilsDown)
      return 'PM Preference is unavailable until all subprojects are complete and Pencils Down is set';
    return '';
  })();

  const boApprovalDisabledTooltip = (() => {
    if (!allPencilsDown)
      return 'BO Approval is unavailable until all subprojects are complete and Pencils Down is set';
    if (!designApproved)
      return 'BO Approval requires PM Preference to be set first';
    return '';
  })();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        mb: '18px',
      }}
    >
      {/* -- Main header container -- */}
      <Box
        sx={{
          bgcolor: 'primary.main',
          borderRadius: 0.5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          px: { xs: 3, sm: 4, md: 5 },
          py: { xs: 2.5, md: 4 },
        }}
      >
        {/* Left: Org name stacked above Project name + FY */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
          <Typography
            sx={{
              fontFamily: FONT_HEADER,
              color: 'text.primary',
              fontSize: { xs: 14, sm: 16, md: 18 },
              fontWeight: 500,
              letterSpacing: '0.5em',
              textTransform: 'uppercase',
              lineHeight: 1.4,
            }}
          >
            {orgName}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'baseline',
              gap: 1.5,
              placeItems: 'center',
              textTransform: 'uppercase',
            }}
          >
            <Typography
              sx={{
                fontFamily: FONT_HEADER,
                color: 'text.primary',
                fontWeight: 800,
                fontSize: { xs: 26, sm: 32, md: 38 },
                lineHeight: 1.1,
                letterSpacing: '0.2em',
              }}
            >
              {projectName}
            </Typography>
            {fiscalYearName && (
              <Typography
                sx={{
                  fontFamily: FONT_HEADER,
                  color: 'info.main',
                  fontSize: { xs: 14, sm: 16, md: 18 },
                  fontWeight: 900,
                  lineHeight: 1.1,
                }}
              >
                {fiscalYearName}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Right: Subproject progress chips + count */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: 3,
            pb: 0.5,
          }}
        >
          {subprojects.length > 0 && subprojects.length <= 10 ? (
            <SubprojectProgressGrid
              subprojects={subprojects}
              onSubprojectChange={onSubprojectChange}
            />
          ) : subprojects.length > 10 ? (
            <SubprojectProgressSummary subprojects={subprojects} />
          ) : null}
          {subprojects.length > 0 && (
            <Box
              sx={{
                bgcolor: alpha(
                  lighten(theme.palette.background.card!, 0.12),
                  0.9,
                ),
                borderRadius: '3px',
                py: '6px',
                minWidth: '100%',
                textAlign: 'center',
              }}
            >
              <Typography
                sx={{
                  fontFamily: FONT_HEADER,
                  color: alpha(theme.palette.common.white, 0.7),
                  fontSize: { xs: 7, sm: 8, md: 9 },
                  fontWeight: 500,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  whiteSpace: 'nowrap',
                }}
              >
                {finishedCount}/{subprojects.length} SUBPROJECTS COMPLETE
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* -- Sub-bar container (separate strip) -- */}
      <Box
        sx={{
          bgcolor: alpha(lighten(theme.palette.background.card!, 0.12), 0.9),
          mt: 3,
          borderRadius: '3px',
          display: 'flex',
          alignItems: 'center',
          px: 2,
          py: 0.75,
          minHeight: 36,
          position: 'relative',
        }}
      >
        {/* Left: Status buttons */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexShrink: 0,
          }}
        >
          {canShowPencilsDown && (
            <Tooltip
              title={pencilsDownDisabledTooltip}
              arrow
              disableHoverListener={!pencilsDownDisabledTooltip}
            >
              <span>
                <Button
                  variant='contained'
                  color={activePencilsDown ? 'primary' : 'secondary'}
                  disableElevation
                  disableRipple
                  size='small'
                  disabled={activePencilsDown && designApproved}
                  startIcon={<Edit sx={{ fontSize: 13 }} />}
                  onClick={() => setPencilsDownDialogOpen(true)}
                  sx={{
                    fontFamily: FONT_HEADER,
                    fontSize: 11,
                    height: 24,
                    borderRadius: '3px',
                    textTransform: 'none',
                    minWidth: 'unset',
                    '& .MuiButton-startIcon': { mr: '6px' },
                  }}
                >
                  {activePencilsDown ? 'Undo Pencils Down' : 'Pencils Down'}
                </Button>
              </span>
            </Tooltip>
          )}
          {isOverviewSelected && perms?.canApproveDesign && (
            <Tooltip
              title={pmPreferenceDisabledTooltip}
              arrow
              disableHoverListener={!pmPreferenceDisabledTooltip}
            >
              <span>
                <Button
                  variant='contained'
                  color={designApproved ? 'primary' : 'secondary'}
                  disabled={!allPencilsDown || budgetApproved}
                  disableElevation
                  disableRipple
                  size='small'
                  startIcon={<Star sx={{ fontSize: 13 }} />}
                  onClick={onPmPreferenceClick}
                  sx={{
                    fontFamily: FONT_HEADER,
                    fontSize: 11,
                    height: 24,
                    borderRadius: '3px',
                    textTransform: 'none',
                    minWidth: 'unset',
                    '& .MuiButton-startIcon': { mr: '6px' },
                  }}
                >
                  PM Preference
                </Button>
              </span>
            </Tooltip>
          )}
          {isOverviewSelected && perms?.canApproveBudget && (
            <Tooltip
              title={boApprovalDisabledTooltip}
              arrow
              disableHoverListener={!boApprovalDisabledTooltip}
            >
              <span>
                <Button
                  variant='contained'
                  color={budgetApproved ? 'primary' : 'secondary'}
                  disabled={!allPencilsDown || !designApproved}
                  disableElevation
                  disableRipple
                  size='small'
                  startIcon={<Star sx={{ fontSize: 13 }} />}
                  onClick={onBoApprovalClick}
                  sx={{
                    fontFamily: FONT_HEADER,
                    fontSize: 11,
                    height: 24,
                    borderRadius: '3px',
                    textTransform: 'none',
                    minWidth: 'unset',
                    '& .MuiButton-startIcon': { mr: '6px' },
                  }}
                >
                  BO Approval
                </Button>
              </span>
            </Tooltip>
          )}
        </Box>

        {/* Center: PROJECT SUMMARY dropdown */}
        <Box
          sx={{
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
          }}
        >
          <Select
            value={
              currentSubprojectId
                ? subprojects.some((sp) => sp.id === currentSubprojectId)
                  ? currentSubprojectId
                  : ''
                : 'overview'
            }
            onChange={handleSelectChange}
            size='small'
            variant='standard'
            IconComponent={DropDownIconComponent}
            disableUnderline
            renderValue={(val) => {
              if (val === 'overview') return 'PROJECT SUMMARY';
              const sp = subprojects.find((s) => s.id === val);
              return sp ? sp.name.toUpperCase() : '';
            }}
            MenuProps={{
              PaperProps: {
                sx: { minWidth: 340, mt: 0.5 },
              },
            }}
            sx={{
              fontFamily: FONT_HEADER,
              bgcolor: 'primary.main',
              color: 'text.primary',
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              borderRadius: '3px',
              minWidth: 260,
              height: '24px !important',
              minHeight: 'unset',
              '.MuiSelect-icon': {
                top: '50%',
                transform: 'translateY(-50%)',
                position: 'absolute',
                right: 6,
              },
              '.MuiSelect-select': {
                px: 2,
                py: '0px !important',
                pr: '30px !important',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                width: '100%',
                height: '100%',
                cursor: 'pointer',
              },
            }}
          >
            <MenuItem
              value='overview'
              sx={{
                py: 1,
                px: 2,
                '&.Mui-selected': { bgcolor: 'transparent' },
                '&.Mui-selected:hover': { bgcolor: 'action.hover' },
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <Box
                sx={{
                  bgcolor: isOverviewSelected
                    ? 'info.main'
                    : 'background.default',
                  borderRadius: '5px',
                  px: 2,
                  py: 1,
                  width: '100%',
                  textAlign: 'center',
                  fontFamily: FONT_HEADER,
                  fontWeight: 600,
                  fontSize: 13,
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                  color: 'text.primary',
                }}
              >
                Project Summary
              </Box>
            </MenuItem>
            {subprojects.map((sp) => (
              <MenuItem
                key={sp.id}
                value={sp.id}
                sx={{
                  py: 1,
                  px: 2,
                  '&.Mui-selected': { bgcolor: 'transparent' },
                  '&.Mui-selected:hover': { bgcolor: 'action.hover' },
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <SubprojectDropdownCard
                  subproject={sp}
                  isSelected={sp.id === currentSubprojectId}
                />
              </MenuItem>
            ))}
          </Select>
        </Box>
      </Box>

      <ConfirmationDialog
        open={pencilsDownDialogOpen}
        icon={<ErrorOutline sx={{ fontSize: 48, color: 'text.primary' }} />}
        title={
          activePencilsDown
            ? isOverviewSelected
              ? `Are you sure you want to undo "Pencils Down" for all subprojects?`
              : `Are you sure you want to undo "Pencils Down" for ${currentSubproject?.name || 'this subproject'}?`
            : isOverviewSelected
              ? `Are you sure you want to set all subprojects as "Pencils Down"?`
              : `Are you sure you want to set ${currentSubproject?.name || 'this subproject'} as "Pencils Down"?`
        }
        message={
          activePencilsDown
            ? 'Users will be able to make edits again.'
            : 'Users will no longer be able to make edits.'
        }
        confirmLabel='Yes'
        cancelLabel='No'
        confirmColor='info'
        onConfirm={handlePencilsDown}
        onCancel={() => setPencilsDownDialogOpen(false)}
      />
    </Box>
  );
}
